import {Action, getModule, Module} from 'vuex-module-decorators';
import store from '@/store';
import {PHeaterMonitorModule, PModuleMeasurement} from '@/views/system/modules/interfaces';
import {PModuleType} from '@/enums/configs';
import {
  PMProcessActionType,
  PMProcessStatus,
  PMProcessSystemType,
} from '@/views/system/modules/enums';
import {EntityModule} from '../entityModule';
import {httpClient} from '@/api/baseApi';
import {getEventData, getEventTypes, getLastEvents} from './base';
import {isNumber} from '@/utils/commonUtils';

/*
    Динамический модуль хранилища vuex для работы с ПМ датчика статуса
*/

const moduleName = 'pheatermonitorSourceModule';

function getMeasurements(elem: any): PModuleMeasurement[] {
  return elem.measurements.map((elem: any) => ({
    measurementId: elem.module_type_measurement_id,
    measurementDescription: elem.module_type_measurement_description,
    topWarningBoundary: elem.top_warning_boundary,
    topDangerBoundary: elem.top_danger_boundary,
    topErrorBoundary: elem.top_error_boundary,
    bottomWarningBoundary: elem.bottom_warning_boundary,
    bottomDangerBoundary: elem.bottom_danger_boundary,
    bottomErrorBoundary: elem.bottom_error_boundary,
  }));
}

function transformGet(elem: any): PHeaterMonitorModule {
  return {
    id: elem.id,
    guid: elem.guid,
    name: elem.name,
    type: PModuleType.HeaterMonitorModule,
    description: elem.description,
    processStatus: elem.process_status as PMProcessStatus,
    processStatusDescription: elem.process_status_description,
    status: elem.status,
    nodes: elem.nodes,
    alias: elem.alias,
    errorCode: elem.error_code,
    measurements: elem.measurements ? getMeasurements(elem) : elem.measurements,
    maxMessageCount: elem.max_message_count,
  };
}

function transformGetById(elem: any): PHeaterMonitorModule {
  return transformGet(elem);
}

function transformCreateEdit(elem: any): PHeaterMonitorModule {
  return {
    id: elem.id,
    guid: elem.guid,
    name: elem.name,
    alias: elem.alias,
    type: PModuleType.HeaterMonitorModule,
    description: elem.description,
    nodes: elem.nodes,
    errorCode: elem.error_code,
    measurements: elem.measurements ? getMeasurements(elem) : elem.measurements,
    maxMessageCount: elem.max_message_count,
  };
}

function transformSave(model: PHeaterMonitorModule): any {
  const measurements: any[] = model.measurements.map((elem: any) => {
    const m: any = {
      module_type_measurement_id: elem.measurementId,
      module_type_measurement_description: elem.measurementDescription,
    };
    if (isNumber(elem.topWarningBoundary)) m.top_warning_boundary = elem.topWarningBoundary;
    if (isNumber(elem.topDangerBoundary)) m.top_danger_boundary = elem.topDangerBoundary;
    if (isNumber(elem.topErrorBoundary)) m.top_error_boundary = elem.topErrorBoundary;
    if (isNumber(elem.bottomWarningBoundary))
      m.bottom_warning_boundary = elem.bottomWarningBoundary;
    if (isNumber(elem.bottomDangerBoundary)) m.bottom_danger_boundary = elem.bottomDangerBoundary;
    if (isNumber(elem.bottomErrorBoundary)) m.bottom_error_boundary = elem.bottomErrorBoundary;
    return m;
  });

  return {
    id: model.id,
    name: model.name,
    description: model.description,
    nodes: model.nodes,
    error_code: model.errorCode,
    measurements: measurements,
    max_message_count: model.maxMessageCount,
  };
}

@Module({
  name: moduleName,
  store: store,
  dynamic: true,
  namespaced: true,
})
export class PHeaterMonitorStoreModule extends EntityModule {
  protected routeName = 'heater-monitor-modules';

  get all(): PHeaterMonitorModule[] {
    return super.all as PHeaterMonitorModule[];
  }

  //Получение списка объектов с сервера
  @Action
  public async fetchAll() {
    return super.fetchAll(transformGet);
  }

  //Получение модели для создания объекта
  @Action
  public async create(): Promise<{data: PHeaterMonitorModule}> {
    return super.create(transformCreateEdit) as Promise<{data: PHeaterMonitorModule}>;
  }

  //Получение модели для редактирования объекта
  @Action
  public async edit(id: any): Promise<{data: PHeaterMonitorModule}> {
    return super.edit(id, transformCreateEdit) as Promise<{data: PHeaterMonitorModule}>;
  }

  //Создание/реактирование объекта
  @Action
  public async save(model: PHeaterMonitorModule) {
    return super.save(model, transformSave);
  }

  //Получение объекта по идентификатору
  @Action
  async getById(id: any): Promise<{data: PHeaterMonitorModule}> {
    return super.getById(id, transformGetById) as Promise<{data: PHeaterMonitorModule}>;
  }

  @Action
  async processAction(params: {id: number; action: PMProcessActionType}) {
    const {data} = await httpClient.put(`/${this.routeName}/${params.id}/processes/`, {
      action: params.action,
    });
    return data;
  }

  // Выполнить системную команду программного модуля
  @Action
  async processSystem(params: {id: number; action: PMProcessSystemType}) {
    const {data} = await httpClient.put(`/${this.routeName}/${params.id}/processes/system`, {
      action: params.action,
    });
    return data;
  }

  //Получение типов событий
  @Action
  async getEventTypes(): Promise<any> {
    try {
      return await getEventTypes(this.routeName);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение списка последних событий
  @Action
  async getLastEvents(params: {id: number; eventType: string; limit?: number}): Promise<any> {
    try {
      return await getLastEvents(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение данных события
  @Action
  async getEventData(params: {id: number; eventType: string; timestamp: number}): Promise<any> {
    try {
      return await getEventData(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }
}

export default getModule(PHeaterMonitorStoreModule);
